import PQueue from 'p-queue';
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Container from '@mui/material/Container';
import { useGridApiRef  } from '@mui/x-data-grid';
import { datadogRum  } from '@datadog/browser-rum';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import grey from '@mui/material/colors/grey';
import orange from '@mui/material/colors/orange';
import Nav from './Nav';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import TermsDialog from './User/TermsDialog';
import DocumentationDialog from './User/DocumentationDialog';
import { useUser, useOrganization, useProjects, useSampleTypes } from './services/api/KeynomeAPIHooks';
import { ProjectContext, ProjectDispatchContext } from './context/ProjectContext';

const uploadQueue = new PQueue({ concurrency: 3 });

const theme = createTheme({
  palette: {
    primary: orange,
    secondary: grey,
    default: grey
  },
});

function Layout() {
    const params = useParams();
    const navigate = useNavigate();

    const { user: auth0User, getAccessTokenSilently } = useAuth0();
    const { activeProject } = useContext(ProjectContext);
    const { setActiveProject } = useContext(ProjectDispatchContext);
    const [ uploadingFiles, setUploadingFiles ] = useState({});
    
    const [termsDialogOpen, setTermsDialogOpen] = useState(false);
    const [showDocs, setShowDocs] = useState(false);

    // snackbar state
    const [progressMessage, setProgressMessage] = useState(null);
    const [userErrorMessage, setUserErrorMessage] = useState(null);
    const [userSuccessMessage, setUserSuccessMessage] = useState(null);

    const datasetsGridRef = useGridApiRef();

    const { data: user, isLoading: userLoading, refetch: refetchUser } = useUser(auth0User?.sub)
    const { data: organization } = useOrganization(user?.attributes.organization_id)
    const { data: projects, refetch: refetchProjects } = useProjects(organization?.id)
    const { data: supportedSampleTypes } = useSampleTypes({ fetchEnabled: !!user })


    useEffect(() => {
      if (!user || userLoading) return;

      datadogRum.setUser({ id: user.id, name: user.attributes.name });

      // If user has not accepted terms, force display of terms of use
      setTermsDialogOpen(!user.attributes.latest_terms_accepted_at);
    }, [user, userLoading]);


    useEffect(() => {
      if (!projects || !projects.length) return;

      // If a project UUID was specified in the URL...
      if (params.projectId) {
        const project = projects.find(p => p.attributes.uuid === params.projectId)
        if (!!project) {
          setActiveProject(project);
          return;
        }
        setUserErrorMessage('Project not found! Please select a different project from the dropdown menu. Contact support@dayzerodiagnostics.com if you require assistance.');
        console.error(`Project with UUID ${params.projectId} not found`);
      }
      // ...otherwise we use the first project (the default project).
      const project = projects[0]
      setActiveProject(project);
    }, [projects, params.projectId, setActiveProject, setUserErrorMessage]);


    const handleBeforeUnload = useCallback((e) => {
      if (Object.keys(uploadingFiles).length > 0) {
        e.preventDefault();
        e.returnValue = "Files are still uploading. If you close this window, file upload will not complete.";
      }
    }, [uploadingFiles]);

    useEffect(() => {
      if (Object.keys(uploadingFiles).length > 0) {
        window.addEventListener('beforeunload', handleBeforeUnload);
      }
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, [uploadingFiles, handleBeforeUnload]);

    const handleUserErrorClose = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }
      setUserErrorMessage(null);
    }
    const handleUserSuccessClose = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }
      setUserSuccessMessage(null);
    }

    const triggerAnalysis = useCallback(concatenatedFastqgzData => { // eslint-disable-line react-hooks/exhaustive-deps
        const concatenatedFastqgzID = concatenatedFastqgzData.id
        return getAccessTokenSilently().then(accessToken => {
            const headers = new Headers({
              Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
            });

            // If an R2 sequencing file was uploaded, this is an illumina sequencing
            // file
            const isIllumina = !!concatenatedFastqgzData.attributes.location_r2_signed_upload_url

            let attributes = {
              'remove_human_dna': true
            }

            if (isIllumina) {
                attributes['generate_amr_predictions'] = true
            } else {
                attributes['rapid_turnaround'] = true
                attributes['generate_amr_finder_plus_res_genes'] = true
                attributes['detect_adapters'] = true
            }


          return fetch(
            `${process.env.REACT_APP_KEYNOME_API_URL_BASE}/v1/concatenated_fastqgzs/${concatenatedFastqgzID}/analyze`,
            {
              method: 'PUT',
              headers,
                body: JSON.stringify(
                    {
                        data: {
                            type: "analyze",
                            attributes: attributes
                        }

                    }
                )
            },
        )

        })
      .then((res) => {
        if (!res.ok) {
          setUserErrorMessage('Failed to trigger analysis in Keynome Cloud! Please wait a few minutes and try again. Contact support@dayzerodiagnostics.com if you require assistance.');
          throw new Error(`Request to ${res.url} failed with ${res.status} (${res.statusText})`)
        }

        setUploadingFiles((existingUploadingFiles) => {
          const newUploadingFiles = {}
          for (const uploadingConcatenatedFastqgzID in existingUploadingFiles) {
              if (parseInt(uploadingConcatenatedFastqgzID) !== concatenatedFastqgzID) {
                  newUploadingFiles[uploadingConcatenatedFastqgzID] = existingUploadingFiles[uploadingConcatenatedFastqgzID]
              }

          }
            return newUploadingFiles
        });
      })
      .catch(datadogRum.addError);
    }, [getAccessTokenSilently])

    const updateUploadProgress = (r2File = null) => {
      const pending = uploadQueue.size + uploadQueue.pending;
      setProgressMessage({
        status: pending > 0 ? 'inProgress' : 'completed',
        message: pending > 0 
          ? `Uploading sequencing data: ${r2File ? Math.ceil(pending / 2) : pending} sample${(r2File ? Math.ceil(pending / 2) : pending) === 1 ? '' : 's'} remaining`
          : `All sequencing files successfully uploaded`
      });
    };
  
    const uploadFile = useCallback((concatenatedFastqgzData, file, r2File) => {
        const newUploadingFiles = {}
        setUploadingFiles((existingUploadingFiles) => {
            for (const [existingConcatenatedFastqgzID, existingConcatenatedFastqgzData] of Object.entries(existingUploadingFiles)) {
                newUploadingFiles[existingConcatenatedFastqgzID] = existingConcatenatedFastqgzData
            }

            newUploadingFiles[concatenatedFastqgzData.id] = concatenatedFastqgzData
            return newUploadingFiles
        });

        setProgressMessage({
          status: 'inProgress',
          message: `Uploading sequencing data: ${Object.keys(newUploadingFiles).length} sample${Object.keys(newUploadingFiles).length === 1 ? '' : 's'}  remaining`
        });

        const uploadWithRetry = async (uploadTask, retries = 3, delay = 1000) => {
          let attempt = 0;
          let error;
  
          while (attempt < retries) {
              try {
                  return await uploadTask();
              } catch (err) {
                  error = err;
                  attempt++;
  
                  if (attempt < retries) {
                      await new Promise(resolve => setTimeout(resolve, delay));
                  }
              }
          }
  
          throw new Error(`Upload failed after ${retries} retries: ${error.message}`);
      };

        const uploadR1 = () => fetch(
          concatenatedFastqgzData.attributes.location_signed_upload_url, 
          { method: 'PUT', body: file }
        ).finally(r2File ? null : updateUploadProgress);
      
        const uploadR2 = r2File ? () => fetch(
          concatenatedFastqgzData.attributes.location_r2_signed_upload_url, 
          { method: 'PUT', body: r2File }
        ).finally(() => updateUploadProgress(r2File)) : null;
      
        const uploads = [uploadQueue.add(() => uploadWithRetry(uploadR1))];
        if (uploadR2) uploads.push(uploadQueue.add(() => uploadWithRetry(uploadR2)));      

        return Promise.all(uploads).then((resArray) => {
            if (!resArray.every(res => res.ok)) throw new Error(resArray);
              return triggerAnalysis(concatenatedFastqgzData)
          })
          .catch(
              (error) => {
                setUserErrorMessage("Failed file upload to Keynome Cloud! Please wait a few minutes and try again. Contact support@dayzerodiagnostics.com if you require assistance.")
                console.error(error)
              }
            )
            .finally(() => {
              updateUploadProgress(r2File); // Ensure message updates after upload completion
          
              if (uploadQueue.size === 0 && uploadQueue.pending === 0) {
                uploadQueue.off('active', updateUploadProgress);
                uploadQueue.off('idle', updateUploadProgress);
              }
          });
    }, [triggerAnalysis]);


  const handleActiveProjectChange = (event) => {
    const newActiveProject = projects.find((project) => project.id === event.target.value);
    const newProjectUrl = new URL(newActiveProject.attributes.keynome_web_url);
    navigate(newProjectUrl.pathname);
  };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;

        }
        setProgressMessage(null)
    }

  let snackbarContent = null
  if (!!progressMessage && progressMessage.status === 'completed') {
      snackbarContent = (
          <Alert
          severity="success"
          variant="filled"
          sx={{ width: '100%'  }}
          >
            {progressMessage.message}
          </Alert>
      )
  } else if (!!progressMessage) {
      snackbarContent = (
        <SnackbarContent 
            message={progressMessage.message}
            action={
                (
              <CircularProgress
                    sx={{marginRight: "10px"}}
                    size={15}
                  />
                )
            }
        />
      )
  }

  return (
    <ThemeProvider theme={theme}>
      <Nav
        projects={projects}
        organization={organization}
        activeProject={activeProject}
        reloadProjects={refetchProjects}
        userData={user ? { data: user } : null }
        setActiveProject={setActiveProject}
        onActiveProjectChange={handleActiveProjectChange}
        datasetsGridRef={datasetsGridRef}
        setShowDocs={setShowDocs}
      />
      <Container maxWidth={false} disableGutters sx={{ overflowX: 'hidden' }}>
        <Outlet context={
            {
                organization,
                userData: user ? { data: user } : null,
                activeProject,
                supportedSampleTypes,
                uploadFile,
                uploadingFilesCt: Object.keys(uploadingFiles).length,
                datasetsGridRef,
                openTermsDialog: () => setTermsDialogOpen(true),
                setUserErrorMessage,
                setUserSuccessMessage,
                setProgressMessage,
                hasAccessReportViruses: organization?.attributes?.metadata?.FF_report_viruses,
                hasAccessClonality: organization?.attributes?.metadata?.FF_clonality,
                setActiveProject,
                handleActiveProjectChange
            }
        } />
        <Snackbar 
          open={!!userErrorMessage}
          autoHideDuration={8000}
          onClose={handleUserErrorClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleUserErrorClose}
            severity="error"
            variant='filled'
            sx={{ width: '100%' }}
          >
            {userErrorMessage}
          </Alert>
        </Snackbar>
        <Snackbar 
          open={!!userSuccessMessage}
          autoHideDuration={8000}
          onClose={handleUserSuccessClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleUserSuccessClose}
            severity="success"
            variant='filled'
            sx={{ width: '100%' }}
          >
            {userSuccessMessage}
          </Alert>
        </Snackbar>
        <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            autoHideDuration={(!!progressMessage && progressMessage.status === 'inProgress') ? null : 5000}      
            open={!!progressMessage}
            onClose={handleSnackbarClose}
        >
          {snackbarContent}
        </Snackbar>
        <TermsDialog isOpen={termsDialogOpen} setTermsDialogOpen={setTermsDialogOpen} user={user} refetchUser={refetchUser} />
        <DocumentationDialog 
        isOpen={showDocs}
        onClose={() => setShowDocs(false)}
      />
      </Container>
      
      <Box 
        component="footer" 
        sx={{
          backgroundColor: grey[50],
          padding: '8px 0',
          marginTop: 'auto',
          fontSize: '0.75rem',
          textAlign: 'center',
          color: grey[600]
        }}
      >
        <Typography variant="caption" component="span">
          Copyright © {new Date().getFullYear()} Day Zero Diagnostics
        </Typography>
        <Typography variant="caption" component="span" sx={{ mx: 1 }}>
          -
        </Typography>
        <Typography variant="caption" component="span">
          Build: {process.env.REACT_APP_KEYNOME_BUILD_VERSION || 'dev'}
        </Typography>
      </Box>
    </ThemeProvider>
  );
}

export default Layout;
